import cx from 'classnames';
import PropTypes from 'prop-types';
import { Range as SlateRange } from 'slate';
import { useReadOnly, useSelected, useSlateStatic } from 'slate-react';

import { COLORS, CSS_CLASS } from 'src/data/ui';
import { SLATE_TEXT_FORMATS, SLATE_VOID_ELEMENT } from 'src/lib/slate';

import { ExternalChannelNode } from './components/ExternalChannelNode';
import { ImageNode } from './components/ImageNode';
import { InstructionNode } from './components/InstructionNode';
import { LinkLeaf } from './components/LinkLeaf';
import { ProductDescriptionNode } from './components/ProductDescriptionNode';
import { ResourceUrlNode } from './components/ResourceUrlNode';
import { VariableNode } from './components/VariableNode';
import { AudioNode } from './views/AudioNode';
import { BankMessageNode } from './views/BankMessageNode';
import { DocumentNode } from './views/DocumentNode';
import { PromptImageNode } from './views/PromptImageNode';
import { PromptTagNode } from './views/PromptTagNode';
import { VideoNode } from './views/VideoNode';

export const CustomElement = ({ attributes, children, element }) => {
  const style = { textAlign: element.align || '' };
  const editor = useSlateStatic();
  const selected = useSelected();
  const readOnly = useReadOnly();

  switch (element.type) {
    case SLATE_TEXT_FORMATS.BLOCK_QUOTE:
      return (
        <blockquote
          style={{
            ...style,
            borderLeft: `solid 3px ${COLORS.primary.light}`,
            paddingLeft: 10,
          }}
          {...attributes}
        >
          {children}
        </blockquote>
      );
    case SLATE_TEXT_FORMATS.BULLETED_LIST:
      return (
        <ul
          style={style}
          {...attributes}
        >
          {children}
        </ul>
      );
    case SLATE_TEXT_FORMATS.NUMBERED_LIST:
      return (
        <ol
          style={style}
          {...attributes}
        >
          {children}
        </ol>
      );
    case SLATE_TEXT_FORMATS.HEADING_ONE:
      return (
        <h1
          style={style}
          {...attributes}
        >
          {children}
        </h1>
      );
    case SLATE_TEXT_FORMATS.HEADING_TWO:
      return (
        <h2
          style={style}
          {...attributes}
        >
          {children}
        </h2>
      );
    case SLATE_TEXT_FORMATS.HEADING_THREE:
      return (
        <h3
          style={style}
          {...attributes}
        >
          {children}
        </h3>
      );
    case SLATE_TEXT_FORMATS.LIST_ITEM:
      return (
        <li
          style={style}
          {...attributes}
          contentEditable={!readOnly}
          suppressContentEditableWarning
        >
          {children}
        </li>
      );
    case SLATE_VOID_ELEMENT.DIVIDER:
      return (
        <div
          style={{
            ...style,
            height: 2,
            width: 50,
            backgroundColor: '#7F91AD',
            margin: '20px 0',
          }}
          {...attributes}
        >
          {children}
        </div>
      );

    case SLATE_TEXT_FORMATS.DD_VARIATION: {
      const variation = element.variation || {};
      const props = {
        'data-variation': '',
        'data-variation-id': variation.id,
        'data-instance-id': variation.instanceId,
      };
      // Used in `useEditorVariationsBoundaries`
      if (variation.isScoreLocked || variation.performancePredictionsScoreLocked) {
        props['data-score-locked'] = '';
      }
      return (
        <div
          style={{ ...style, margin: '8px 0px' }}
          {...props}
          {...attributes}
        >
          {children}
        </div>
      );
    }

    case SLATE_TEXT_FORMATS.LINK:
      return (
        <LinkLeaf
          style={style}
          attributes={attributes}
          element={element}
        >
          {children}
        </LinkLeaf>
      );

    case SLATE_TEXT_FORMATS.IMG:
      return (
        <ImageNode
          style={style}
          attributes={attributes}
          element={element}
        >
          {children}
        </ImageNode>
      );

    case SLATE_TEXT_FORMATS.BANK_MESSAGE:
      return (
        <BankMessageNode
          element={element}
          attributes={attributes}
        >
          {children}
        </BankMessageNode>
      );

    case SLATE_TEXT_FORMATS.PROMPT_IMAGE:
      return (
        <PromptImageNode
          element={element}
          attributes={attributes}
        >
          {children}
        </PromptImageNode>
      );

    case SLATE_TEXT_FORMATS.PROMPT_DOCUMENT:
      return (
        <DocumentNode
          element={element}
          attributes={attributes}
        >
          {children}
        </DocumentNode>
      );

    case SLATE_TEXT_FORMATS.PRODUCT_DESCRIPTION:
      return (
        <ProductDescriptionNode
          element={element}
          attributes={attributes}
        >
          {children}
        </ProductDescriptionNode>
      );

    case SLATE_TEXT_FORMATS.RESOURCE_URL:
      return (
        <ResourceUrlNode
          element={element}
          attributes={attributes}
        >
          {children}
        </ResourceUrlNode>
      );

    case SLATE_TEXT_FORMATS.EXTERNAL_CHANNEL_TEXT:
      return (
        <ExternalChannelNode
          element={element}
          attributes={attributes}
        >
          {children}
        </ExternalChannelNode>
      );

    case SLATE_TEXT_FORMATS.PROMPT_TAG:
      return (
        <PromptTagNode
          element={element}
          attributes={attributes}
        >
          {children}
        </PromptTagNode>
      );

    case SLATE_TEXT_FORMATS.PROMPT_VARIABLE:
      return (
        <VariableNode
          element={element}
          attributes={attributes}
        >
          {children}
        </VariableNode>
      );

    case SLATE_TEXT_FORMATS.PROMPT_INSTRUCTION:
      return (
        <InstructionNode
          element={element}
          attributes={attributes}
        >
          {children}
        </InstructionNode>
      );

    case SLATE_TEXT_FORMATS.PROMPT_AUDIO:
      return (
        <AudioNode
          element={element}
          {...attributes}
        >
          {children}
        </AudioNode>
      );

    case SLATE_TEXT_FORMATS.PROMPT_VIDEO:
      return (
        <VideoNode
          element={element}
          {...attributes}
        >
          {children}
        </VideoNode>
      );

    default: {
      const selection = editor.selection;
      const isSelectionCollapsed = selection ? SlateRange.isCollapsed(selection) : false;
      const childrenNodes = element?.children || [];
      const isEmpty =
        childrenNodes.length === 0 || (childrenNodes.length === 1 && childrenNodes[0]?.text === '');

      return (
        <p
          className={cx(attributes.className, {
            [CSS_CLASS.focusedEmptyParagraph]: selected && isSelectionCollapsed && isEmpty,
          })}
          style={style}
          {...attributes}
        >
          {children}
        </p>
      );
    }
  }
};

CustomElement.propTypes = {
  attributes: PropTypes.shape().isRequired,
  children: PropTypes.arrayOf(PropTypes.node).isRequired,
  element: PropTypes.shape().isRequired,
};
